class MemberInfoValidation {
    constructor() {
        this.memberData = {
            name: '',
            address: '',
            dob: ''
        };
        this.pendingAction = null;
        this.pendingForm = null;
        this.locale = this.detectLocale();
        this.translations = this.loadTranslations();

        // Email eligibility API configuration
        this.emailValidationConfig = {
            enabled: true,
            apiUrl: (window.API_URL || '') + 'b2cs',
            method: 'GET'
        };

        this.init();
    }

    detectLocale() {
        const htmlLang = document.documentElement.lang;
        if (htmlLang && htmlLang.startsWith('fr')) {
            return 'fr';
        }
        if (htmlLang && htmlLang.startsWith('de')) {
            return 'de';
        }
        return 'en';
    }

    loadTranslations() {
        const translations = {
            en: {
                firstname_required: 'First name is required',
                lastname_required: 'Last name is required',
                email_required: 'Email address is required',
                email_invalid: 'Please enter a valid email address',
                email_not_eligible: 'This email address is already used for a membership.',
                email_check_failed: 'Unable to verify email eligibility. Please try again.',
                address_required: 'Address is required',
                city_required: 'City is required',
                zipcode_required: 'Zip code is required',
                country_required: 'Country is required',
                dob_required: 'Date of birth is required',
                dob_future: 'Date of birth cannot be in the future',
                general: 'Please complete all required fields correctly',
                prop_firstname: 'Member first name',
                prop_lastname: 'Member last name',
                prop_email: 'Member email',
                prop_address: 'Member address',
                prop_apartment: 'Member apartment',
                prop_city: 'Member city',
                prop_zipcode: 'Member zip code',
                prop_country: 'Member country',
                prop_dob: 'Member date of birth'
            },
            fr: {
                firstname_required: 'Le prénom est obligatoire',
                lastname_required: 'Le nom est obligatoire',
                email_required: "L'adresse e-mail est obligatoire",
                email_invalid: 'Veuillez saisir une adresse e-mail valide',
                email_not_eligible: 'Cet e-mail est déjà utilisé pour une adhésion',
                email_check_failed: "L'autorisation de l'e-mail n'a pas pu être vérifiée. Veuillez réessayer.",
                address_required: "L'adresse est obligatoire",
                city_required: 'La ville est obligatoire',
                zipcode_required: 'Le code postal est obligatoire',
                country_required: 'Le pays est obligatoire',
                dob_required: 'La date de naissance est obligatoire',
                dob_future: 'La date de naissance ne peut pas être dans le futur',
                general: 'Veuillez remplir correctement tous les champs obligatoires',
                prop_firstname: 'Prénom du membre',
                prop_lastname: 'Nom du membre',
                prop_email: 'E-mail du membre',
                prop_address: 'Adresse du membre',
                prop_apartment: 'Appartement du membre',
                prop_city: 'Ville du membre',
                prop_zipcode: 'Code postal du membre',
                prop_country: 'Pays du membre',
                prop_dob: 'Date de naissance du membre'
            },
            de: {
                firstname_required: 'Vorname ist erforderlich',
                lastname_required: 'Nachname ist erforderlich',
                email_required: 'E-Mail-Adresse ist erforderlich',
                email_invalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
                email_not_eligible: 'Diese E-mail wird bereits für eine Mitgliedschaft verwendet',
                email_check_failed: 'Die E-Mail-Berechtigung konnte nicht überprüft werden. Bitte versuchen Sie es erneut.',
                address_required: 'Adresse ist erforderlich',
                city_required: 'Stadt ist erforderlich',
                zipcode_required: 'Postleitzahl ist erforderlich',
                country_required: 'Land ist erforderlich',
                dob_required: 'Geburtsdatum ist erforderlich',
                dob_future: 'Das Geburtsdatum darf nicht in der Zukunft liegen',
                general: 'Bitte füllen Sie alle erforderlichen Felder korrekt aus',
                prop_firstname: 'Mitglied Vorname',
                prop_lastname: 'Mitglied Nachname',
                prop_email: 'Mitglied E-Mail',
                prop_address: 'Mitglied Adresse',
                prop_apartment: 'Mitglied Wohnung',
                prop_city: 'Mitglied Stadt',
                prop_zipcode: 'Mitglied Postleitzahl',
                prop_country: 'Mitglied Land',
                prop_dob: 'Mitglied Geburtsdatum'
            }
        };
        return translations[this.locale] || translations.en;
    }

    t(key) {
        return this.translations[key] || key;
    }

    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupValidation());
        } else {
            this.setupValidation();
        }
    }

    setupValidation() {
        const productForms = document.querySelectorAll('form[data-type="add-to-cart-form"]');

        productForms.forEach(form => {
            form.addEventListener('submit', (e) => this.handleFormSubmit(e), true);

            const submitButtons = form.querySelectorAll('button[type="submit"], input[type="submit"], button[name="add"], .shopify-payment-button button, [data-shopify="payment-button"] button');
            submitButtons.forEach(button => {
                button.addEventListener('click', (e) => this.handleButtonClick(e, form), true);
            });

            const dynamicCheckoutButtons = document.querySelectorAll('.shopify-payment-button button, [data-shopify="payment-button"] button, .dynamic-checkout__content button');
            dynamicCheckoutButtons.forEach(button => {
                button.addEventListener('click', (e) => {
                    const closestForm = button.closest('form') || form;
                    this.handleButtonClick(e, closestForm);
                }, true);
            });
        });

        this.setupModal();
        this.observeForDynamicButtons();
    }

    handleFormSubmit(event) {
        const form = event.target;

        if (!this.isMembershipProduct(form)) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        this.showModal(form, 'submit');
        return false;
    }

    handleButtonClick(event, form) {
        if (!this.isMembershipProduct(form)) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        const isDynamicCheckout = this.isDynamicCheckoutButton(event.target);
        this.showModal(form, isDynamicCheckout ? 'dynamic-checkout' : 'add-to-cart');
        return false;
    }

    isMembershipProduct(form) {
        const productCard = form.closest('.card-wrapper');
        const productInfo = form.closest('product-info');

        let productData = null;

        if (productCard) {
            const jsonEl = productCard.querySelector('.data-product-json');
            if (jsonEl) {
                try {
                    productData = JSON.parse(jsonEl.textContent);
                } catch (e) {
                    console.warn('Failed to parse product JSON from card', e);
                }
            }
        }

        if (!productData && productInfo) {
            const productId = productInfo.getAttribute('data-product-id');
            if (window.productData && window.productData[productId]) {
                productData = window.productData[productId];
            }
        }

        if (productData) {
            if (productData && productData.is_membership) {
                return productData.is_membership === true ||
                       productData.is_membership === 1 ||
                       productData.is_membership === "1";
            }
        }

        return false;
    }

    isDynamicCheckoutButton(button) {
        const dynamicSelectors = [
            '.shopify-payment-button',
            '[data-shopify="payment-button"]',
            '.dynamic-checkout__content'
        ];

        return dynamicSelectors.some(selector =>
            button.closest(selector) !== null
        );
    }

    showModal(form, actionType) {
        this.pendingForm = form;
        this.pendingAction = actionType;

        const modal = document.getElementById('member-info-modal');
        if (modal) {
            modal.style.display = 'flex';
            document.body.style.overflow = 'hidden';

            const firstInput = modal.querySelector('input');
            if (firstInput) {
                firstInput.focus();
            }
        }
    }

    setupModal() {
        const modal = document.getElementById('member-info-modal');
        if (!modal) return;

        const closeBtn = modal.querySelector('.member-modal__close');
        const cancelBtn = modal.querySelector('.member-modal__cancel');
        const continueBtn = modal.querySelector('.member-modal__continue');
        const backdrop = modal.querySelector('.member-modal__backdrop');

        [closeBtn, cancelBtn, backdrop].forEach(element => {
            if (element) {
                element.addEventListener('click', () => this.hideModal());
            }
        });

        if (continueBtn) {
            continueBtn.addEventListener('click', () => this.processPurchase());
        }

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modal.style.display === 'flex') {
                this.hideModal();
            }
        });
    }

    hideModal() {
        const modal = document.getElementById('member-info-modal');
        if (modal) {
            modal.style.display = 'none';
            document.body.style.overflow = '';
            this.clearModalErrors();
        }
    }

    async processPurchase() {
        // Clear any previous general errors before validation
        this.hideModalGeneralError();

        if (this.validateModalFields()) {
            const continueBtn = document.querySelector('.member-modal__continue');
            const originalBtnText = continueBtn.textContent;

            // Check email eligibility if enabled
            if (this.emailValidationConfig.enabled) {
                const modal = document.getElementById('member-info-modal');
                const emailInput = modal.querySelector('input[name="properties[Email Adresse]"]');
                const email = emailInput.value.trim();

                // Show loading state
                continueBtn.disabled = true;
                continueBtn.textContent = this.locale === 'fr' ? 'Vérification...' : (this.locale === 'de' ? 'Überprüfung...' : 'Checking...');

                const isEligible = await this.checkEmailEligibility(email);

                // Reset button state
                continueBtn.disabled = false;
                continueBtn.textContent = originalBtnText;

                if (!isEligible) {
                    return;
                }
            }

            await this.addMemberDataToForm();
            this.hideModal();

            // Proceed with add to cart action
            this.proceedWithOriginalAction();
        }
    }

    validateModalFields() {
        const modal = document.getElementById('member-info-modal');
        const firstNameInput = modal.querySelector('input[name="properties[Vorname]"]');
        const lastNameInput = modal.querySelector('input[name="properties[Nachname]"]');
        const emailInput = modal.querySelector('input[name="properties[Email Adresse]"]');
        const addressInput = modal.querySelector('input[name="properties[Adresse]"]');
        const cityInput = modal.querySelector('input[name="properties[Stadt]"]');
        const zipCodeInput = modal.querySelector('input[name="properties[Postleitzahl]"]');
        const countryInput = modal.querySelector('select[name="properties[Land]"]');
        const dobInput = modal.querySelector('input[name="properties[Geburtsdatum]"]');

        let isValid = true;

        if (!firstNameInput.value.trim()) {
            this.showModalFieldError(firstNameInput, this.t('firstname_required'));
            isValid = false;
        } else {
            this.hideModalFieldError(firstNameInput);
        }

        if (!lastNameInput.value.trim()) {
            this.showModalFieldError(lastNameInput, this.t('lastname_required'));
            isValid = false;
        } else {
            this.hideModalFieldError(lastNameInput);
        }

        if (!emailInput.value.trim()) {
            this.showModalFieldError(emailInput, this.t('email_required'));
            isValid = false;
        } else if (!this.isValidEmail(emailInput.value)) {
            this.showModalFieldError(emailInput, this.t('email_invalid'));
            isValid = false;
        } else {
            this.hideModalFieldError(emailInput);
        }

        if (!addressInput.value.trim()) {
            this.showModalFieldError(addressInput, this.t('address_required'));
            isValid = false;
        } else {
            this.hideModalFieldError(addressInput);
        }

        if (!cityInput.value.trim()) {
            this.showModalFieldError(cityInput, this.t('city_required'));
            isValid = false;
        } else {
            this.hideModalFieldError(cityInput);
        }

        if (!zipCodeInput.value.trim()) {
            this.showModalFieldError(zipCodeInput, this.t('zipcode_required'));
            isValid = false;
        } else {
            this.hideModalFieldError(zipCodeInput);
        }

        if (!countryInput.value.trim()) {
            this.showModalFieldError(countryInput, this.t('country_required'));
            isValid = false;
        } else {
            this.hideModalFieldError(countryInput);
        }

        if (!dobInput.value.trim()) {
            this.showModalFieldError(dobInput, this.t('dob_required'));
            isValid = false;
        } else {
            const selectedDate = new Date(dobInput.value);
            const today = new Date();
            today.setHours(23, 59, 59, 999);

            if (selectedDate > today) {
                this.showModalFieldError(dobInput, this.t('dob_future'));
                isValid = false;
            } else {
                this.hideModalFieldError(dobInput);
            }
        }

        if (!isValid) {
            this.showModalGeneralError(this.t('general'));
        } else {
            this.hideModalGeneralError();
        }

        return isValid;
    }

    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    calculateAge(dobValue) {
        const dob = new Date(dobValue);
        const today = new Date();

        let age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();

        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            age--;
        }

        return age < 0 ? 0 : age;
    }

    getAgeGroup(age, variants = null) {
        if (variants && variants.length > 0) {
            const ageRangePattern = /(\d+)\s*-\s*(\d+)|>\s*(\d+)|<\s*(\d+)/;
            const potentialAgeGroups = [];

            variants.forEach(variant => {
                [variant.option1, variant.option2, variant.option3].forEach(option => {
                    if (option && ageRangePattern.test(option)) {
                        const match = option.match(/(\d+)\s*-\s*(\d+)/);
                        const greaterThan = option.match(/>\s*(\d+)/);
                        const lessThan = option.match(/<\s*(\d+)/);

                        if (match) {
                            const min = parseInt(match[1]);
                            const max = parseInt(match[2]);
                            if (age >= min && age <= max) {
                                potentialAgeGroups.push(option);
                            }
                        } else if (greaterThan) {
                            const threshold = parseInt(greaterThan[1]);
                            if (age > threshold) {
                                potentialAgeGroups.push(option);
                            }
                        } else if (lessThan) {
                            const threshold = parseInt(lessThan[1]);
                            if (age < threshold) {
                                potentialAgeGroups.push(option);
                            }
                        }
                    }
                });
            });

            if (potentialAgeGroups.length > 0) {
                return potentialAgeGroups[0];
            }
        }

        if (age >= 0 && age <= 6) return "0 - 6";
        if (age >= 7 && age <= 16) return "7 - 16";
        if (age > 16) return "> 16";
        return null;
    }

    selectAgeGroup(dobValue) {
        const age = this.calculateAge(dobValue);

        let variants = null;
        const productCard = this.pendingForm.closest('.card-wrapper');
        const productInfo = this.pendingForm.closest('product-info');

        if (productCard) {
            const jsonEl = productCard.querySelector('.data-product-json');
            if (jsonEl) {
                try {
                    const productData = JSON.parse(jsonEl.textContent);
                    variants = productData.variants;
                } catch (e) {
                    console.warn('Could not parse product data for dynamic age groups');
                }
            }
        } else if (productInfo) {
            const productId = productInfo.getAttribute('data-product-id');
            if (window.productData && window.productData[productId]) {
                variants = window.productData[productId].variants;
            }
        }

        const group = this.getAgeGroup(age, variants);

        if (!group) return Promise.resolve();

        let radio = this.pendingForm.querySelector(
            `.product-form__input input[type="radio"][value="${group}"]`
        );

        if (!radio) {
            radio = document.querySelector(
                `.product-form__input input[type="radio"][value="${group}"]`
            );
        }

        if (radio) {
            radio.checked = true;
            radio.click();

            const changeEvent = new Event('change', { bubbles: true });
            radio.dispatchEvent(changeEvent);

            return new Promise((resolve) => {
                setTimeout(resolve, 200);
            });
        } else {
            this.selectVariantByAgeGroup(group);
            return Promise.resolve();
        }
    }

    selectVariantByAgeGroup(ageGroup) {
        const productCard = this.pendingForm.closest('.card-wrapper');
        const productInfo = this.pendingForm.closest('product-info');

        let productData = null;

        if (productCard) {
            const jsonEl = productCard.querySelector('.data-product-json');
            if (jsonEl) {
                try {
                    productData = JSON.parse(jsonEl.textContent);
                } catch (e) {
                    console.warn('Failed to parse product JSON from card', e);
                }
            }
        }

        if (!productData && productInfo) {
            const productId = productInfo.getAttribute('data-product-id');
            if (window.productData && window.productData[productId]) {
                productData = window.productData[productId];
            }
        }

        if (productData && productData.variants) {
            const matchingVariant = productData.variants.find(variant => {
                const normalize = (str) => str ? str.trim().replace(/\s+/g, '') : '';

                const normalizedAgeGroup = normalize(ageGroup);

                const option1Trimmed = variant.option1 ? variant.option1.trim() : null;
                const option2Trimmed = variant.option2 ? variant.option2.trim() : null;
                const option3Trimmed = variant.option3 ? variant.option3.trim() : null;
                const ageGroupTrimmed = ageGroup.trim();

                const matches = variant.option1 === ageGroup ||
                               variant.option2 === ageGroup ||
                               variant.option3 === ageGroup ||
                               option1Trimmed === ageGroupTrimmed ||
                               option2Trimmed === ageGroupTrimmed ||
                               option3Trimmed === ageGroupTrimmed ||
                               normalize(variant.option1) === normalizedAgeGroup ||
                               normalize(variant.option2) === normalizedAgeGroup ||
                               normalize(variant.option3) === normalizedAgeGroup;

                return matches;
            });

            if (matchingVariant) {
                this.updateFormVariantId(matchingVariant.id);
            } else {
                console.warn('No variant found for age group:', ageGroup);
            }
        } else {
            console.warn('No product data with variants available');
        }
    }

    updateFormVariantId(variantId) {
        const variantIdInput = this.pendingForm.querySelector('input[name="id"]');
        if (variantIdInput) {
            variantIdInput.value = variantId;
            const changeEvent = new Event('change', { bubbles: true });
            variantIdInput.dispatchEvent(changeEvent);
        } else {
            console.warn('Could not find variant ID input in form');
        }
    }

    async addMemberDataToForm() {
        const modal = document.getElementById('member-info-modal');
        const firstNameValue = modal.querySelector('input[name="properties[Vorname]"]').value;
        const lastNameValue = modal.querySelector('input[name="properties[Nachname]"]').value;
        const emailValue = modal.querySelector('input[name="properties[Email Adresse]"]').value;
        const addressValue = modal.querySelector('input[name="properties[Adresse]"]').value;
        const apartmentValue = modal.querySelector('input[name="properties[Wohnung]"]').value;
        const cityValue = modal.querySelector('input[name="properties[Stadt]"]').value;
        const zipCodeValue = modal.querySelector('input[name="properties[Postleitzahl]"]').value;
        const countryValue = modal.querySelector('select[name="properties[Land]"]').value;
        const dobValue = modal.querySelector('input[name="properties[Geburtsdatum]"]').value;

        if(dobValue){
            await this.selectAgeGroup(dobValue);
        }

        const existingInputs = this.pendingForm.querySelectorAll('input[name^="properties["], textarea[name^="properties["], select[name^="properties["]');
        existingInputs.forEach(input => input.remove());

        const hiddenInputs = [
            { name: 'properties[Vorname]', value: firstNameValue },
            { name: 'properties[Nachname]', value: lastNameValue },
            { name: 'properties[Email Adresse]', value: emailValue },
            { name: 'properties[Adresse]', value: addressValue },
            { name: 'properties[Stadt]', value: cityValue },
            { name: 'properties[Postleitzahl]', value: zipCodeValue },
            { name: 'properties[Land]', value: countryValue },
            { name: 'properties[Geburtsdatum]', value: dobValue }
        ];

        if (apartmentValue.trim()) {
            hiddenInputs.push({ name: 'properties[Wohnung]', value: apartmentValue });
        }

        hiddenInputs.forEach(inputData => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = inputData.name;
            input.value = inputData.value;
            this.pendingForm.appendChild(input);
        });
    }

    async proceedWithOriginalAction() {
        if (!this.pendingForm) return;

        // Add a unique identifier to prevent cart items from being combined
        const uniqueInput = document.createElement('input');
        uniqueInput.type = 'hidden';
        uniqueInput.name = 'properties[_membership_id]';
        uniqueInput.value = Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        this.pendingForm.appendChild(uniqueInput);

        if (this.pendingAction === 'dynamic-checkout') {
            this.submitFormWithCheckoutRedirect();
        } else {
            // Use AJAX to add to cart and open cart drawer
            await this.addToCartViaAjax();
        }
    }

    async addToCartViaAjax() {
        const formData = new FormData(this.pendingForm);
        const cartDrawer = document.querySelector('cart-drawer');

        // Add sections to request so Shopify returns updated cart HTML
        if (cartDrawer) {
            formData.append(
                'sections',
                cartDrawer.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            cartDrawer.setActiveElement(document.activeElement);
        }

        try {
            const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error('Failed to add to cart');
            }

            const data = await response.json();

            // Check for errors
            if (data.status) {
                console.error('Cart error:', data);
                this.pendingForm.submit();
                return;
            }

            // Render the cart drawer with updated content (this will also open it)
            if (cartDrawer && data.sections) {
                // Remove is-empty class from cart-drawer element before rendering
                // This prevents focus trap errors when opening an empty cart
                if (cartDrawer.classList.contains('is-empty')) {
                    cartDrawer.classList.remove('is-empty');
                }

                cartDrawer.renderContents(data);
            } else if (cartDrawer) {
                // If no sections in response, reload the page to show cart
                window.location.reload();
            }
        } catch (error) {
            console.error('Error adding membership to cart:', error);

            // Fallback to traditional form submit if AJAX fails
            this.pendingForm.submit();
        }
    }

    submitFormWithCheckoutRedirect() {
        const form = this.pendingForm;

        const returnToInput = document.createElement('input');
        returnToInput.type = 'hidden';
        returnToInput.name = 'return_to';
        returnToInput.value = '/checkout';
        form.appendChild(returnToInput);

        setTimeout(() => {
            form.submit();
        }, 500);
    }

    observeForDynamicButtons() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) {
                        const paymentButtons = node.querySelectorAll ? node.querySelectorAll('.shopify-payment-button button, [data-shopify="payment-button"] button, .dynamic-checkout__content button') : [];
                        paymentButtons.forEach(button => {
                            button.addEventListener('click', (e) => {
                                const form = button.closest('form') || document.querySelector('form[data-type="add-to-cart-form"]');
                                if (form) {
                                    this.handleButtonClick(e, form);
                                }
                            }, true);
                        });
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    showModalFieldError(field, message) {
        field.setAttribute('aria-invalid', 'true');
        field.style.borderColor = 'rgb(215, 44, 13)';

        const errorId = field.getAttribute('aria-describedby');
        if (errorId) {
            const errorElement = document.getElementById(errorId);
            if (errorElement) {
                errorElement.classList.remove('hidden');
                const errorMessageSpan = errorElement.querySelector('.error-message');
                if (errorMessageSpan) {
                    errorMessageSpan.textContent = message;
                }
            }
        }
    }

    hideModalFieldError(field) {
        field.removeAttribute('aria-invalid');
        field.style.borderColor = '';

        const errorId = field.getAttribute('aria-describedby');
        if (errorId) {
            const errorElement = document.getElementById(errorId);
            if (errorElement) {
                errorElement.classList.add('hidden');
            }
        }
    }

    showModalGeneralError(message) {
        const modal = document.getElementById('member-info-modal');
        const errorElement = modal.querySelector('.member-modal__error');
        const errorMessageSpan = modal.querySelector('.member-modal__error-message');

        if (errorElement && errorMessageSpan) {
            errorMessageSpan.textContent = message;
            errorElement.classList.remove('hidden');
        }
    }

    hideModalGeneralError() {
        const modal = document.getElementById('member-info-modal');
        const errorElement = modal.querySelector('.member-modal__error');

        if (errorElement) {
            errorElement.classList.add('hidden');
        }
    }

    clearModalErrors() {
        const modal = document.getElementById('member-info-modal');
        const allInputs = modal.querySelectorAll('.field__input');

        allInputs.forEach(input => {
            this.hideModalFieldError(input);
        });

        this.hideModalGeneralError();
    }

    async checkEmailEligibility(email) {
        try {
            const config = this.emailValidationConfig;
            const url = `${config.apiUrl}?filter[email]=${encodeURIComponent(email)}&filter[isAppVersion]=true`;

            // Use global Authorization header from global-variables.liquid
            const headers = {};
            if (window.API_Header_Options && window.API_Header_Options.headers) {
                Object.assign(headers, window.API_Header_Options.headers);
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) {
                console.error('Email eligibility check failed:', response.status);
                // Show API error
                this.showModalGeneralError(this.t('email_check_failed'));
                return false;
            }

            const data = await response.json();

            // Check if data exists and has at least one record
            if (data.data && data.data.length > 0) {
                const memberData = data.data[0];
                const membership = memberData.attributes?.membership;

                // If membership is an object (not empty array), user already has active membership
                if (membership && typeof membership === 'object' && !Array.isArray(membership)) {
                    // User has active membership - not eligible for new membership
                    // Show error in the general error section above the buttons
                    this.showModalGeneralError(this.t('email_not_eligible'));
                    return false;
                }

                // membership is [] (empty array) or doesn't exist - eligible
                return true;
            }

            // No data found - eligible (new user)
            return true;

        } catch (error) {
            console.error('Error checking email eligibility:', error);
            // On error, show message and don't proceed
            this.showModalGeneralError(this.t('email_check_failed'));
            return false;
        }
    }
}

new MemberInfoValidation();